/*
 * The end-of-visit order review screen (owner decision 2026-10-20). Pure: no React, no network.
 *
 * BlinkScribe's order suggestions v2 payload (N3/orderV2.js) carries, for providers with the review screen
 * (BlinkScribe review.py: BS_ORDER_REVIEW_USERS, default Dr. Madhu in 9928, its own kill switch):
 *   suggestions[i].tier            "heard" | "suggested"
 *   suggestions[i].review_checked  the default checkbox (tier thresholds / check-all policy are BlinkScribe's)
 *   suggestions[i].review_reason   a lab's chart reason ("Labs today; last CMP 120 days ago; on ACEi/ARB")
 *   suggestions[i].tier "hidden"   a lab suggestion while no labs are drawn today: not on the screen
 *   review: { enabled, v, config, labs_drawn, labs_model,
 *             panel: { concept, components },              // the in-house panel and the labs it contains
 *             lab_suggestions: [ suggestion-shaped rows ],  // labs the lab tiers add (panel rule, single rules)
 *             due_with_labs: [{ concept, rule, line: "guideline" | "interval", order_name, variants,
 *                               order_type: "lab", days_since, interval_days, basis, reason, checked }] }
 * Sections of the screen:
 *   heard      "Heard in the conversation" (pre-checked)
 *   suggested  "Suggested" (checked per the policy)
 *   due        "Due while labs are drawn": only while at least one lab is checked in heard / suggested; its
 *              rows are placed only while the section shows; guideline-due first, then "his usual interval"
 *   (panel)    while the in-house panel is ticked its component labs are hidden from suggested / due
 *   placed     "Already ordered" (owner 10-20): BlinkScribe's already_placed + any suggestion already on the visit
 *              + the visit's own clinical orders (orders/get: labs, injections, procedures, tests, imaging,
 *              referrals, vitamins; not cancelled / hidden), shown with the suggestions as checked, greyed,
 *              disabled rows. They are never in the selection: Confirm cannot place them again.
 *   nothing    "Nothing missing" when no new order is left to tick (nothingMissing); with heard placed rows (owner
 *              10-21) "Nothing missing — everything discussed is already ordered (n heard)"
 *   discussed  "Discussed - not ordered" (greyed)
 * Nothing is placed until the provider presses Confirm; then only the checked rows, one at a time, through the
 * tab's own add (the same as clicking ⊕ on each). Per visit, a ledger remembers what the review placed so a
 * second Confirm (retry, double click, another open) never places the same order twice.
 */
import { orderKey } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderKey";
import { evidenceTone } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderV2";
import { defaultWhen, whenFor } from "./orderWhen";

export const REVIEW_PREFIX = "careiq:orderReview:";
export const reviewStoreKey = (patientId, apptKey) => (patientId && apptKey ? `${REVIEW_PREFIX}${patientId}:${apptKey}` : null);
export const visitKeyOf = (patientId, apptKey) => (patientId && apptKey ? `${patientId}:${apptKey}` : null);

export const SECTION_TITLES = {
  heard: "Heard in the conversation",
  suggested: "Suggested",
  due: "Due while labs are drawn",
  placed: "Already ordered",
  discussed: "Discussed · not ordered",
};

export const reviewEnabled = (payload) => Boolean(
  payload && payload.status === "ok" && payload.review && payload.review.enabled === true && Array.isArray(payload.suggestions),
);

// The per-visit record: { state: "pending" | "confirmed" | "dismissed", checks: {rowId: bool}, picks: {rowId: name},
// placed: {rowId: {key, order_id}}, autoShown: bool }. Ids, concept ids and catalogue order names only.
// voiceRemoved: {concept: true} rows "Ordered by voice" the provider removed during the recording (VoiceOrdersPanel).
// The live Orders card (liveOrdersModel.js, owner 10-23): liveOrdered {concept: {order_id, at, name, ordertype}}
// what its Order button placed, liveDismissed {concept: true} rows the provider dismissed, liveShown {concept: 1}.
// when: {concept: "today" | "schedule"} the provider's Today / Schedule tap (orderWhen.js, owner 10-24), shared by
// the live card and the review.
// Rows whose conversation evidence says the order was declined / already resulted are never pre-ticked here,
// whatever the payload's review_checked says (10-30: 5 such rows were pre-ticked for one provider in a week).
export const NEVER_PRECHECK_EVIDENCE = new Set(["declined", "past_result"]);

// The record after Cancel / close / "Sign without these orders" (10-30): state "dismissed" (not pending: sign-off
// does not reopen it) and every shown row the provider did not tick himself stored unticked, so a review reopened
// later (chart button, a later stop) shows nothing pre-ticked. His own ticks, picks and the placed ledger stay.
export const dismissedRecord = (rec, sections) => {
  const cur = rec || emptyRecord();
  const checks = { ...(cur.checks || {}) };
  const rows = sections ? [...(sections.heard || []), ...(sections.suggested || []), ...(sections.due || [])] : [];
  rows.forEach((r) => {
    if (r && r.id && !Object.prototype.hasOwnProperty.call(checks, r.id)) checks[r.id] = false;
  });
  return { ...cur, state: cur.state === "confirmed" ? "confirmed" : "dismissed", checks };
};

export const emptyRecord = () => ({
  state: "pending", checks: {}, picks: {}, placed: {}, voiceRemoved: {}, autoShown: false,
  liveOrdered: {}, liveDismissed: {}, liveShown: {}, when: {},
});
export const readRecord = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    const r = raw ? JSON.parse(raw) : null;
    if (!r || typeof r !== "object") return null;
    return {
      ...emptyRecord(), ...r, checks: { ...(r.checks || {}) }, picks: { ...(r.picks || {}) }, placed: { ...(r.placed || {}) },
      voiceRemoved: { ...(r.voiceRemoved || {}) },
      liveOrdered: { ...(r.liveOrdered || {}) }, liveDismissed: { ...(r.liveDismissed || {}) }, liveShown: { ...(r.liveShown || {}) },
      when: { ...(r.when && typeof r.when === "object" ? r.when : {}) },
    };
  } catch (_) {
    return null;
  }
};
export const writeRecord = (key, rec) => {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(rec)); } catch (_) { /* non-fatal */ }
};

const isLabRow = (concept, orderType) => String(orderType || "").toLowerCase() === "lab" || /^lab_/.test(String(concept || ""));

const variantsOf = (s) => {
  const v = Array.isArray(s.variants) ? s.variants.filter((x) => typeof x === "string" && x.trim()) : [];
  return v.length ? v : [s.order_name].filter(Boolean);
};

/*
 * The screen's rows. rec = the visit's record (checks / picks / placed ledger); isAdded(key) = already on the
 * visit (orders/get + the tab's ledger). A row: { id, section, concept, key, name, variants, order_type, isLab,
 * reason, basis, evidenceLabel, tone, confidence, checked, when: "today" | "schedule", whenDefault }.
 */
// The visit's orders that count as "Already ordered" (care ordertype strings; Notes, VISIT, Tasks, Rx... do not).
const CLINICAL_ORDERTYPE = /^(labs?|injections?|procedures?(\/tests)?|tests|diagnostic tests?|imaging|referrals?|vitamins?)$/;
export const isLiveClinicalOrder = (o) => {
  if (!o || typeof o !== "object") return false;
  const t = String(o.ordertype || "").toLowerCase().trim();
  const st = String(o.status || "").toUpperCase();
  return CLINICAL_ORDERTYPE.test(t) && !st.startsWith("CANCEL") && String(o.is_hide || "").toUpperCase() !== "Y";
};

// Concepts BlinkScribe heard ordered / agreed in the visit AND already placed today (payload flags only).
export const heardPlacedConcepts = (payload) => {
  const out = new Set();
  if (!payload || typeof payload !== "object") return out;
  (Array.isArray(payload.already_placed) ? payload.already_placed : []).forEach((p) => {
    if (p && p.concept && p.heard === true) out.add(p.concept);
  });
  (Array.isArray(payload.heard_placed) ? payload.heard_placed : []).forEach((c) => { if (typeof c === "string" && c) out.add(c); });
  return out;
};

export const buildSections = (payload, { rec = emptyRecord(), aliases = {}, isAdded = () => false, visitOrders = [] } = {}) => {
  const out = { heard: [], suggested: [], due: [], placed: [], discussed: [], hiddenByPanel: [] };
  if (!reviewEnabled(payload)) return out;
  const placedConcepts = new Set();
  const placedKeys = new Set();
  const seen = new Set();
  // "Heard ✓ already ordered" (owner 10-21): BlinkScribe flags a placed concept the visit's talk also ordered /
  // agreed (already_placed[].heard, heard_placed[]). Old payloads carry neither: no row is ever tagged.
  const heardSet = heardPlacedConcepts(payload);
  // the concept of a visit order care only knows by name: any payload row (suggestion, lab rule, already placed,
  // discussed, live card row) whose name or variant keys the same (live 10-22/23 #5: such rows were listed but
  // never counted as concepts)
  const conceptByKey = new Map();
  const review0 = payload.review || {};
  const live0 = payload.live_orders && Array.isArray(payload.live_orders.rows) ? payload.live_orders.rows : [];
  [...(payload.suggestions || []), ...(review0.lab_suggestions || []), ...(payload.already_placed || []),
    ...(payload.discussed || []), ...live0].forEach((s) => {
    if (!s || !s.concept) return;
    [s.order_name, ...(Array.isArray(s.variants) ? s.variants : [])].forEach((n) => {
      const k = orderKey(n, aliases);
      if (k && !conceptByKey.has(k)) conceptByKey.set(k, s.concept);
    });
  });
  // order = { orderId, when } when the visit order is known (orders/get or the live card's record): the row's
  // Today / Schedule can then be changed (owner 10-24)
  const placedRow = (concept0, name, order = null) => {
    const key = orderKey(name, aliases);
    const concept = concept0 || (key && conceptByKey.get(key)) || null;
    const heard = Boolean(concept) && heardSet.has(concept);
    if ((concept && placedConcepts.has(concept)) || (key && placedKeys.has(key))) {
      if (concept) placedConcepts.add(concept);
      const prev = out.placed.find((r) => (r.concept && r.concept === concept) || (key && r.key === key));
      if (prev && heard) prev.heard = true;
      if (prev && order && order.orderId != null && prev.orderId == null) Object.assign(prev, order);
      return;
    }
    if (concept) placedConcepts.add(concept);
    if (key) placedKeys.add(key);
    out.placed.push({
      id: `p:${concept || key}`, section: "placed", concept: concept || null, key, name, checked: true, heard,
      orderId: order && order.orderId != null ? order.orderId : null, when: (order && order.when) || null,
    });
  };
  (payload.already_placed || []).forEach((p) => { if (p && p.concept && p.order_name) placedRow(p.concept, p.order_name); });
  const row = (s, section, id, extra) => {
    const variants = variantsOf(s);
    const pick = rec.picks[id];
    const name = String((pick && variants.includes(pick) ? pick : s.order_name) || variants[0] || "").trim();
    const key = orderKey(name, aliases);
    if (!key) return null;
    // on the visit already under this name or any of the concept's other names (a hand-typed "CBC w/ diff")
    if (rec.placed[id] || isAdded(key) || variants.some((v) => v !== name && isAdded(orderKey(v, aliases)))) {
      placedRow(s.concept, name);
      return null;
    }
    if (seen.has(key)) return null;
    seen.add(key);
    const dflt = Boolean(extra.dflt);
    return {
      id, section, concept: s.concept, key, name, variants,
      order_type: s.order_type || null, isLab: isLabRow(s.concept, s.order_type),
      reason: extra.reason || "", basis: extra.basis || "", evidenceLabel: extra.evidenceLabel || "",
      tone: extra.tone || "chart", confidence: typeof s.confidence === "number" ? s.confidence : null,
      checked: Object.prototype.hasOwnProperty.call(rec.checks, id) ? Boolean(rec.checks[id]) : dflt,
      // Today / Schedule (owner 10-24): the provider's tap (live card or here) over BlinkScribe's default
      when: whenFor(rec, s), whenDefault: defaultWhen(s),
    };
  };
  const review = payload.review || {};
  const labExtra = Array.isArray(review.lab_suggestions) ? review.lab_suggestions : [];
  const voiceRemoved = (rec && rec.voiceRemoved) || {};
  const liveDismissed = (rec && rec.liveDismissed) || {};
  // what the live Orders card placed: already ordered (the visit's orders may not show it yet)
  Object.entries((rec && rec.liveOrdered) || {}).forEach(([c, o]) => {
    if (o && o.name) placedRow(c, o.name, o.order_id != null ? { orderId: o.order_id, when: o.when || "today" } : null);
  });
  [...payload.suggestions, ...labExtra].forEach((s) => {
    if (!s || !s.concept || placedConcepts.has(s.concept) || s.tier === "hidden") return;
    // "Ordered by voice" (BlinkScribe voice.py): a row the provider removed in the live panel is not offered
    if (s.voice === true && voiceRemoved[s.concept]) return;
    // the live Orders card: a row the provider dismissed there is not offered again
    if (liveDismissed[s.concept]) return;
    const section = s.tier === "heard" ? "heard" : "suggested";
    const r = row(s, section, `s:${s.concept}`, {
      dflt: Boolean(s.review_checked) && !NEVER_PRECHECK_EVIDENCE.has(s.evidence),
      reason: s.review_reason || [s.reason, s.chart_reason].filter(Boolean).join(" · "),
      evidenceLabel: s.evidence_label || s.evidence || "", tone: evidenceTone(s.evidence),
    });
    if (r) out[section].push(r);
  });
  (review.due_with_labs || []).forEach((d) => {
    if (!d || !d.concept || placedConcepts.has(d.concept) || liveDismissed[d.concept]) return;
    const r = row({ ...d, order_type: d.order_type || "lab" }, "due", `d:${d.concept}`, {
      dflt: d.checked, reason: d.reason || "", basis: d.basis || "", evidenceLabel: "Due", tone: "chart",
    });
    if (r) out.due.push({ ...r, line: d.line === "interval" ? "interval" : "guideline" });
  });
  // The in-house panel contains its component labs: while the panel is ticked, the suggested and due rows of
  // those labs are hidden (and never placed); what was said in the room stays.
  const panel = review.panel && review.panel.concept ? review.panel : null;
  const panelOn = Boolean(panel) && [...out.heard, ...out.suggested].some((r) => r.concept === panel.concept && r.checked);
  if (panelOn) {
    const comp = new Set(Array.isArray(panel.components) ? panel.components : []);
    out.hiddenByPanel = [...out.suggested, ...out.due].filter((r) => comp.has(r.concept)).map((r) => r.concept);
    out.suggested = out.suggested.filter((r) => !comp.has(r.concept));
    out.due = out.due.filter((r) => !comp.has(r.concept));
  } else {
    out.hiddenByPanel = [];
  }
  // the visit's own clinical orders not named by BlinkScribe (placed after the final pass, outside its catalogue)
  (Array.isArray(visitOrders) ? visitOrders : []).forEach((o) => {
    if (!isLiveClinicalOrder(o)) return;
    const name = String(o.ordername || o.description || o.name || "").trim();
    if (name) placedRow(null, name, o.id != null ? { orderId: o.id, when: String(o.schedule || "") === "t" ? "schedule" : "today" } : null);
  });
  (payload.discussed || []).forEach((d) => {
    if (d && d.concept && d.order_name && !placedConcepts.has(d.concept)) {
      out.discussed.push({ id: `x:${d.concept}`, section: "discussed", concept: d.concept, name: d.order_name, evidenceLabel: d.evidence_label || d.evidence || "" });
    }
  });
  return out;
};

// "Due while labs are drawn" shows only while a lab is checked in Heard / Suggested.
export const dueVisible = (sections) => [...sections.heard, ...sections.suggested].some((r) => r.checked && r.isLab);

// "Nothing missing": no new order left to tick (every suggestion is already on the visit, or there were none).
export const nothingMissing = (sections) => sections.heard.length + sections.suggested.length === 0
  && !(dueVisible(sections) && sections.due.length > 0);

// How many "Already ordered" rows were also heard in the visit (0 for old payloads).
export const heardPlacedCount = (sections) => sections.placed.filter((r) => r.heard).length;

// Rows left to decide on the screen (heard + suggested + visible due). 0 = the review is not opened by itself
// (owner 10-23: no empty panels, no "Confirm · no orders").
export const leftoverCount = (sections) => shownRows(sections).length;

// The "Nothing missing" line (shown only while nothingMissing).
export const nothingMissingText = (sections) => {
  const n = heardPlacedCount(sections);
  if (n > 0) return `Nothing missing — everything discussed is already ordered (${n} heard)`;
  return sections.placed.length
    ? "Nothing missing · every order from this visit is already placed."
    : "Nothing missing · no orders to add for this visit.";
};

// What Confirm places: the checked rows of the sections on screen.
export const selection = (sections) => [
  ...sections.heard.filter((r) => r.checked),
  ...sections.suggested.filter((r) => r.checked),
  ...(dueVisible(sections) ? sections.due.filter((r) => r.checked) : []),
];

export const shownRows = (sections) => [
  ...sections.heard, ...sections.suggested, ...(dueVisible(sections) ? sections.due : []),
];

/*
 * Confirm: places `rows` one at a time with place(row) -> Promise<boolean> (the tab's add). Idempotent per
 * visit + order: a row whose id is in the ledger, or whose key is already on the visit (isAdded), is skipped;
 * the same key twice in one batch is placed once. onResult(row, status) after each row. Returns
 * { placed: [rows], failed: [rows], skipped: [rows] }. Never throws (a throwing place counts as a failure).
 */
export const confirmRows = async (rows, { place, ledger = {}, isAdded = () => false, onResult = () => {} } = {}) => {
  const res = { placed: [], failed: [], skipped: [] };
  const keys = new Set();
  for (const r of rows) {
    if (ledger[r.id] || isAdded(r.key) || keys.has(r.key)) {
      res.skipped.push(r);
      onResult(r, "skipped");
      continue;
    }
    keys.add(r.key);
    let ok = false;
    try {
      // eslint-disable-next-line no-await-in-loop
      ok = (await place(r)) === true;
    } catch (_) {
      ok = false;
    }
    if (ok) res.placed.push(r);
    else res.failed.push(r);
    onResult(r, ok ? "placed" : "failed");
  }
  return res;
};

export const concepts = (rows) => [...new Set(rows.map((r) => r.concept).filter(Boolean))];

/*
 * "Ordered by voice" during the recording (BlinkScribe voice.py; VoiceOrdersPanel). The live rows are kept in the
 * visit's review record so the end-of-visit screen opens with the provider's choices: unticked = checks[s:<concept>]
 * false (the row stays, unchecked); removed = voiceRemoved[concept] (not offered at all). Pure.
 */
export const VOICE_REASON = "Ordered by voice";
export const voiceRowId = (concept) => `s:${concept}`;
export const voiceRowsFor = (vo, patientId, apptKey) => {
  if (!vo || typeof vo !== "object" || !Array.isArray(vo.rows)) return [];
  if (patientId != null && vo.patientid != null && String(vo.patientid) !== String(patientId)) return [];
  if (apptKey != null && vo.appointmentid != null && String(vo.appointmentid) !== String(apptKey)) return [];
  return vo.rows.filter((r) => r && typeof r.concept === "string" && r.concept && typeof r.order_name === "string");
};
export const voiceChecked = (rec, concept) => {
  const id = voiceRowId(concept);
  return rec && rec.checks && Object.prototype.hasOwnProperty.call(rec.checks, id) ? Boolean(rec.checks[id]) : true;
};
export const voiceSetChecked = (rec, concept, on) => {
  const r = { ...emptyRecord(), ...(rec || {}) };
  return { ...r, checks: { ...(r.checks || {}), [voiceRowId(concept)]: Boolean(on) } };
};
export const voiceRemove = (rec, concept) => {
  const r = voiceSetChecked(rec, concept, false);
  return { ...r, voiceRemoved: { ...(r.voiceRemoved || {}), [concept]: true } };
};
export const voiceRestore = (rec, concept) => {
  const r = voiceSetChecked(rec, concept, true);
  const vr = { ...(r.voiceRemoved || {}) };
  delete vr[concept];
  return { ...r, voiceRemoved: vr };
};
